import { useEffect } from "react";

/**
 * SeoHead — imperative SEO manager (no react-helmet dep).
 * Sets <title>, meta description, canonical, OG tags and injects a
 * JSON-LD structured-data block for the current page.
 *
 * Usage:
 *   <SeoHead
 *     title="Maltora Premium Malt · SBC Ltd Brewing"
 *     description="Premium malt drink brewed in Manchester…"
 *     canonical="/brands/maltora"
 *     image="/icons/icon-512.png"
 *     jsonLd={{ "@context": "https://schema.org", "@type": "Product", … }}
 *   />
 */
export default function SeoHead({ title, description, canonical, image, jsonLd }) {
  useEffect(() => {
    if (title) document.title = title;

    const setMeta = (name, content, attr = "name") => {
      if (!content) return;
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    if (description) {
      setMeta("description", description);
      setMeta("og:description", description, "property");
      setMeta("twitter:description", description);
    }
    if (title) {
      setMeta("og:title", title, "property");
      setMeta("twitter:title", title);
    }
    if (image) {
      const abs = image.startsWith("http") ? image : `${window.location.origin}${image}`;
      setMeta("og:image", abs, "property");
      setMeta("twitter:image", abs);
    }
    setMeta("og:type", "website", "property");
    setMeta("twitter:card", "summary_large_image");

    if (canonical) {
      const href = canonical.startsWith("http") ? canonical : `${window.location.origin}${canonical}`;
      let link = document.head.querySelector("link[rel='canonical']");
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", href);
      setMeta("og:url", href, "property");
    }

    // JSON-LD structured data
    const scriptId = "sbc-jsonld";
    let script = document.getElementById(scriptId);
    if (jsonLd) {
      if (!script) {
        script = document.createElement("script");
        script.type = "application/ld+json";
        script.id = scriptId;
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    } else if (script) {
      script.remove();
    }

    return () => {
      const s = document.getElementById(scriptId);
      if (s) s.remove();
    };
  }, [title, description, canonical, image, JSON.stringify(jsonLd)]);

  return null;
}
